import React, { useEffect, useState, useCallback, useRef } from "react";
import { Select, Spin } from "antd";
import styled from "styled-components";

const StyledSelect = styled(Select)`
  min-width: 200px;

  ${(props) =>
    props.$formatLabel &&
    `
    .ant-select-selection-item {
      line-height: 20px !important;
    }
    .ant-select-selector{
      min-height: fit-content
    }
  `}
`;

const CustomSelectField = ({
  label,
  fetchOptions,
  onChange,
  value,
  initialValue,
  formatLabel = false, 
}) => {
  const [selectedValue, setSelectedValue] = useState(initialValue);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setSelectedValue(initialValue); 
  }, [initialValue]);

  const isFetching = useRef(false);

  const loadOptions = useCallback(
    async (pageNumber, query = "") => {
      if (isFetching.current || (!hasMore && pageNumber !== 0)) return;

      isFetching.current = true;
      setLoading(true);

      try {
        const newOptions = await fetchOptions(pageNumber, query);
        setOptions((prevOptions) =>
          pageNumber === 0 ? newOptions : [...prevOptions, ...newOptions]
        );
        setHasMore(newOptions.length > 0);
        setPage(pageNumber);
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        isFetching.current = false;
        setLoading(false);
      }
    },
    [fetchOptions, hasMore]
  );

  useEffect(() => {
    if (!isFetching.current) {
      const handler = setTimeout(() => {
        loadOptions(0, searchQuery);
      }, 1000);
      return () => clearTimeout(handler);
    }
  }, [loadOptions, searchQuery]);


  const handleChange = (value) => {
    setSelectedValue(value);
    if (onChange) onChange(value);
  };

  return (
    <StyledSelect
      placeholder={`Select ${label}`}
      $formatLabel={formatLabel}
      value={selectedValue}
      onSearch={(value) => {
        setSearchQuery(value);
        setPage(0);
        setHasMore(true);
      }}
      onFocus={() => {
        // Clear search term and reload options when the field is focused
        if(searchQuery !== ""){
          setSearchQuery("");
          loadOptions(0, "");
        }else{
          loadOptions(0, searchQuery);
        }
      }}
      showSearch
      onChange={handleChange}
      onPopupScroll={(e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollTop + clientHeight >= scrollHeight - 20) {
          loadOptions(page + 1, searchQuery);
        }
      }}
      loading={loading}
      filterOption={false}
      allowClear
    >
      {options?.map((option) => (
        <Select.Option key={option.value} value={option.value}>
          {formatLabel ? formatLabel(option) : option.label}
        </Select.Option>
      ))}
      {loading && (
        <Select.Option key="loading" disabled>
          <Spin size="small" /> Loading...
        </Select.Option>
      )}
    </StyledSelect>
  );
};

export default CustomSelectField;
